// MyPosts.js - User's own blog posts with status tracking
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogApi } from '../../Services/Api';
import styles from './MyPosts.module.css';

const MyPosts = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    fetchMyPosts();
  }, []);

  const fetchMyPosts = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getMyPosts();
      setPosts(response.data.posts);
      setSummary(response.data.summary);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    setDeleting(postId);
    try {
      await blogApi.deletePost(postId);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setDeleting(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: { icon: '📝', label: 'Draft', className: styles.statusDraft },
      pending: { icon: '⏳', label: 'Pending Review', className: styles.statusPending },
      approved: { icon: '✅', label: 'Published', className: styles.statusApproved },
      rejected: { icon: '❌', label: 'Rejected', className: styles.statusRejected }
    };
    return badges[status] || badges.draft;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredPosts = filter === 'all' 
    ? posts 
    : posts.filter(p => p.status === filter);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate('/blog')}>
          ← Back to Blog
        </button>
        <div className={styles.headerContent}>
          <h1>📄 My Posts</h1>
          <button 
            className={styles.newPostButton}
            onClick={() => navigate('/blog/write')}
          >
            ✍️ Write New Post
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className={styles.summaryStats}>
        <div 
          className={`${styles.statCard} ${filter === 'all' ? styles.active : ''}`}
          onClick={() => setFilter('all')}
        >
          <span className={styles.statNumber}>{summary.total || 0}</span>
          <span className={styles.statLabel}>Total</span>
        </div>
        <div 
          className={`${styles.statCard} ${filter === 'approved' ? styles.active : ''}`}
          onClick={() => setFilter('approved')}
        >
          <span className={styles.statNumber}>{summary.approved || 0}</span>
          <span className={styles.statLabel}>✅ Published</span>
        </div>
        <div 
          className={`${styles.statCard} ${filter === 'pending' ? styles.active : ''}`}
          onClick={() => setFilter('pending')}
        >
          <span className={styles.statNumber}>{summary.pending || 0}</span>
          <span className={styles.statLabel}>⏳ Pending</span>
        </div>
        <div 
          className={`${styles.statCard} ${filter === 'draft' ? styles.active : ''}`}
          onClick={() => setFilter('draft')}
        >
          <span className={styles.statNumber}>{summary.draft || 0}</span>
          <span className={styles.statLabel}>📝 Drafts</span>
        </div>
        <div 
          className={`${styles.statCard} ${filter === 'rejected' ? styles.active : ''}`}
          onClick={() => setFilter('rejected')}
        >
          <span className={styles.statNumber}>{summary.rejected || 0}</span>
          <span className={styles.statLabel}>❌ Rejected</span>
        </div>
      </div>

      {/* Posts List */}
      <div className={styles.postsList}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Loading your posts...</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyIcon}>📝</span>
            <h3>{filter === 'all' ? "You haven't written any posts yet" : `No ${filter} posts`}</h3>
            <p>Share your journey with the community!</p>
            <button onClick={() => navigate('/blog/write')}>
              Write Your First Post
            </button>
          </div>
        ) : (
          filteredPosts.map(post => {
            const statusBadge = getStatusBadge(post.status);
            return (
              <div key={post.id} className={styles.postCard}>
                <div className={styles.postHeader}>
                  <span className={`${styles.statusBadge} ${statusBadge.className}`}>
                    {statusBadge.icon} {statusBadge.label}
                  </span>
                  <span className={styles.postDate}>{formatDate(post.created_at)}</span>
                </div>
                
                <h3 className={styles.postTitle}>{post.title}</h3>
                <p className={styles.postExcerpt}>{post.excerpt}</p>
                
                {/* Rejection Reason */}
                {post.status === 'rejected' && post.rejection_reason && (
                  <div className={styles.rejectionReason}>
                    <strong>Reason:</strong> {post.rejection_reason}
                  </div>
                )}
                
                {/* Post Stats (for published) */}
                {post.status === 'approved' && (
                  <div className={styles.postStats}>
                    <span>❤️ {post.like_count} likes</span>
                    <span>👁️ {post.view_count} views</span>
                  </div>
                )}
                
                {/* Actions */}
                <div className={styles.postActions}>
                  {post.status === 'approved' ? (
                    <button 
                      className={styles.viewButton}
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      View Post
                    </button>
                  ) : (
                    <>
                      <button 
                        className={styles.editButton}
                        onClick={() => navigate(`/blog/edit/${post.id}`)}
                      >
                        ✏️ Edit
                      </button>
                      <button 
                        className={styles.deleteButton}
                        onClick={() => handleDelete(post.id)}
                        disabled={deleting === post.id}
                      >
                        {deleting === post.id ? 'Deleting...' : '🗑️ Delete'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MyPosts;
